/**
 * Tipi TypeScript per il modulo Schede Dinamiche.
 */

// ============ FIELD TYPES ============

export type FieldType =
  | 'text' | 'textarea' | 'number' | 'date' | 'select' | 'checkbox' | 'radio'
  | 'table' | 'section' | 'calculated' | 'signature' | 'photo' | 'file' | 'readonly'
  | 'db_lookup' | 'label'
  // Phase 2
  | 'opcua_read' | 'opcua_write' | 'modbus_read' | 'modbus_write'

export interface VisibilityRule {
  field_id: string
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'not_empty' | 'empty'
  value?: any
  logic?: 'and' | 'or'
}

export interface TableColumn {
  id: string
  label: string
  type: 'text' | 'number' | 'date' | 'select' | 'checkbox'
  required?: boolean
  options?: { label: string; value: string }[]
  width?: string
  calculation_formula?: string
}

export interface FieldValidation {
  min?: number
  max?: number
  minLength?: number
  maxLength?: number
  pattern?: string
  pattern_message?: string
}

export interface MachineConfig {
  protocol: 'opcua' | 'modbus'
  direction: 'read' | 'write'
  endpoint?: string
  node_id?: string
  register_address?: number
  register_type?: 'holding' | 'input' | 'coil'
  data_type?: 'int16' | 'int32' | 'float32' | 'bool'
  scale_factor?: number
  polling_interval_ms?: number
}

export interface SchemaField {
  id: string
  type: FieldType
  label: string
  name: string
  ordine: number
  required: boolean
  placeholder?: string
  defaultValue?: any
  width?: 'full' | 'half' | 'third'
  help_text?: string

  // Select/Radio
  options?: { label: string; value: string }[]

  // Visibility
  visibility_rules?: VisibilityRule[]

  // Calculated
  calculation_formula?: string
  calculation_precision?: number

  // Table
  table_columns?: TableColumn[]
  table_min_rows?: number
  table_max_rows?: number

  // Section
  section_collapsible?: boolean
  section_default_open?: boolean
  outside_section?: boolean  // sgancia il campo dalla sezione precedente

  // Validation
  validation?: FieldValidation

  // File/Photo
  file_accept?: string[]
  file_max_size_mb?: number

  // Readonly
  readonly_value?: string

  // Label (pure text, no input)
  label_text?: string  // testo mostrato (se diverso dalla label)

  // DB Lookup
  db_lookup_entity?: string  // chiave da DB_LOOKUP_ENTITIES

  // Date → Scadenza automatica
  crea_scadenza?: boolean
  crea_scadenza_assegnazione?: 'owner' | 'seleziona'  // owner=proprietario entità, seleziona=picker utente

  // Machine (Phase 2)
  machine_config?: MachineConfig
}

// ============ TEMPLATE ============

export type EditPolicy = 'editable' | 'view_only'

export interface SchedaTemplateList {
  id: string
  codice: string
  nome: string
  descrizione: string | null
  categoria: string | null
  entity_types: string[]
  versione: number
  is_published: boolean
  is_active: boolean
  template_documento_id: string | null
  edit_policy: EditPolicy
  istanze_count: number
  fields_count: number
  created_at: string | null
  updated_at: string | null
}

export interface SchedaTemplateDetail extends SchedaTemplateList {
  schema_json: SchemaField[]
  published_at: string | null
  created_by: string | null
}

// ============ VERSIONE ============

export interface SchedaVersioneItem {
  id: string
  template_id: string
  numero_versione: number
  note_versione: string | null
  fields_count: number
  creato_da: string | null
  created_at: string | null
}

// ============ ISTANZA ============

export type StatoIstanza = 'bozza' | 'compilata' | 'firmata' | 'archiviata'

export interface SchedaIstanzaList {
  id: string
  codice: string
  template_id: string
  template_nome: string
  versione_numero: number
  entity_type: string
  entity_id: string
  entity_label: string | null
  stato: StatoIstanza
  compilato_da: string | null
  compilato_da_nome: string | null
  has_firma: boolean
  edit_policy: EditPolicy
  created_at: string | null
  completata_at: string | null
}

export interface SchedaIstanzaDetail extends SchedaIstanzaList {
  versione_id: string
  schema_frozen: SchemaField[]
  dati: Record<string, any>
  firma_semplice: string | null
  firma_semplice_data: string | null
  firma_semplice_nominativo: string | null
  documento_generato_id: string | null
  updated_at: string | null
}

// ============ FIELD PALETTE ============

export interface FieldPaletteItem {
  type: FieldType
  label: string
  icon: string  // lucide icon name
  category: 'base' | 'avanzato' | 'layout' | 'machine'
  phase: 1 | 2
}

export const FIELD_PALETTE: FieldPaletteItem[] = [
  // Base
  { type: 'text', label: 'Testo', icon: 'type', category: 'base', phase: 1 },
  { type: 'textarea', label: 'Testo lungo', icon: 'align-left', category: 'base', phase: 1 },
  { type: 'number', label: 'Numero', icon: 'hash', category: 'base', phase: 1 },
  { type: 'date', label: 'Data', icon: 'calendar', category: 'base', phase: 1 },
  { type: 'select', label: 'Selezione', icon: 'list', category: 'base', phase: 1 },
  { type: 'checkbox', label: 'Checkbox', icon: 'check-square', category: 'base', phase: 1 },
  { type: 'radio', label: 'Scelta singola', icon: 'circle-dot', category: 'base', phase: 1 },
  // Avanzato
  { type: 'table', label: 'Tabella', icon: 'table', category: 'avanzato', phase: 1 },
  { type: 'calculated', label: 'Calcolato', icon: 'calculator', category: 'avanzato', phase: 1 },
  { type: 'signature', label: 'Firma', icon: 'pen-tool', category: 'avanzato', phase: 1 },
  { type: 'photo', label: 'Foto', icon: 'camera', category: 'avanzato', phase: 1 },
  { type: 'file', label: 'File', icon: 'paperclip', category: 'avanzato', phase: 1 },
  { type: 'readonly', label: 'Solo lettura', icon: 'eye', category: 'avanzato', phase: 1 },
  { type: 'db_lookup', label: 'Ricerca DB', icon: 'search', category: 'avanzato', phase: 1 },
  // Layout
  { type: 'label', label: 'Etichetta', icon: 'tag', category: 'layout', phase: 1 },
  { type: 'section', label: 'Sezione', icon: 'layout', category: 'layout', phase: 1 },
  // Machine (Phase 2 - visibili ma disabilitati)
  { type: 'opcua_read', label: 'OPC-UA Read', icon: 'cpu', category: 'machine', phase: 2 },
  { type: 'opcua_write', label: 'OPC-UA Write', icon: 'cpu', category: 'machine', phase: 2 },
  { type: 'modbus_read', label: 'Modbus Read', icon: 'hard-drive', category: 'machine', phase: 2 },
  { type: 'modbus_write', label: 'Modbus Write', icon: 'hard-drive', category: 'machine', phase: 2 },
]

// ============ DB LOOKUP ENTITIES ============

export const DB_LOOKUP_ENTITIES: Record<string, string> = {
  clienti: 'Clienti',
  offerte: 'Offerte',
  commesse: 'Commesse',
  strumenti: 'Strumenti / Macchinari',
  utenti: 'Utenti',
  listino: 'Voci di Listino',
  personale: 'Personale',
  fornitori: 'Fornitori',
}

// ============ ENTITY TYPE LABELS ============

export const ENTITY_TYPE_LABELS: Record<string, string> = {
  fase_commessa: 'Fase Commessa',
  promemoria: 'Promemoria',
  strumento: 'Strumento/Macchinario',
  azione_fascicolo: 'Azione Fascicolo',
  evento_calendario: 'Evento Calendario',
}

export const STATO_ISTANZA_CONFIG: Record<StatoIstanza, { label: string; color: string; bg: string }> = {
  bozza: { label: 'Bozza', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  compilata: { label: 'Compilata', color: 'text-blue-700', bg: 'bg-blue-100' },
  firmata: { label: 'Firmata', color: 'text-green-700', bg: 'bg-green-100' },
  archiviata: { label: 'Archiviata', color: 'text-gray-700', bg: 'bg-gray-100' },
}

// ============ HELPERS ============

/** Crea un campo vuoto con defaults per il tipo dato */
export function createDefaultField(type: FieldType, ordine: number): SchemaField {
  const id = crypto.randomUUID()
  const label = FIELD_PALETTE.find(p => p.type === type)?.label || type

  const base: SchemaField = {
    id,
    type,
    label,
    name: `campo_${id.slice(0, 8)}`,
    ordine,
    required: false,
    width: 'full',
  }

  // Defaults specifici per tipo
  switch (type) {
    case 'select':
    case 'radio':
      base.options = [
        { label: 'Opzione 1', value: 'opt1' },
        { label: 'Opzione 2', value: 'opt2' },
      ]
      break
    case 'table':
      base.table_columns = [
        { id: crypto.randomUUID(), label: 'Colonna 1', type: 'text' },
        { id: crypto.randomUUID(), label: 'Colonna 2', type: 'text' },
      ]
      base.table_min_rows = 1
      base.table_max_rows = 50
      break
    case 'section':
      base.section_collapsible = true
      base.section_default_open = true
      break
    case 'calculated':
      base.calculation_formula = ''
      base.calculation_precision = 2
      break
    case 'number':
      base.validation = {}
      break
    case 'file':
      base.file_max_size_mb = 10
      break
    case 'photo':
      base.file_accept = ['.jpg', '.jpeg', '.png', '.heic']
      base.file_max_size_mb = 10
      break
    case 'db_lookup':
      base.db_lookup_entity = 'clienti'
      break
    case 'label':
      base.label_text = ''
      break
  }

  return base
}
